import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  BriefcaseBusiness, Building2, Check, ChevronDown, ChevronUp,
  Database, Download, Factory, Filter, Globe2, Languages, Layers, Loader2, MapPin, Plus, RotateCcw, Wrench,
  Search, ShieldCheck, SlidersHorizontal, UserRound, Info, X,
} from "lucide-react";
import { UrlContext } from "../../contexts/UrlContext";
import expertDefaultImg from "../../images/expert-default.png";
import industryLogo from "../../images/default_industry.png";

/*
  Database Search V2 — the complete front-end lives in this single file.
  Integration:
    <DatabaseSearchDrawer
      projectID={projectID}
      onClose={() => setOpenDatabaseSearch(false)}
      onAddToProject={handleDatabaseSearchAdd} // See ProjectDetails.jsx
    />
  V2 backend must be registered under /api/database-search-v2.
  API_URL comes from your existing UrlContext, e.g. https://backend.example/api/.
*/
const EMPTY_OPTIONS = { categories: [], industries: [], skills: [], languages: [], projectLanguages: [], countries: [], regions: [] };
const CACHE_MS = 90_000;
const INK = "#365561";
const CRM_IMAGE_HOST = "https://images.expertpowerhouse.com";
const validIndustryOption = value => {
  const name = String(value || '').trim();
  return name.length > 1 && !/[.,]/.test(name) && name.toLowerCase() !== 'no industry preference';
};

// Follow the same CRM storage rules as ProfilePrintContent and ProfilePage.
// LinkedIn and full image URLs are already absolute; CRM storage paths need a host.
function resolveCrmImage(value) {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^(data:image\/)/i.test(raw)) return raw;
  return `${CRM_IMAGE_HOST}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

const cls = (...parts) => parts.filter(Boolean).join(" ");
function readToken() {
  try { return JSON.parse(localStorage.getItem("eph-token") || "null")?.value || ""; }
  catch { return ""; }
}
function normalizeApiRoot(url) {
  return `${String(url || "").replace(/\/+$/, "")}/database-search-v2`;
}
function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function Highlight({ text, terms, className = "" }) {
  const content = String(text || "");
  const matcher = useMemo(() => {
    const clean = [...new Set((terms || []).filter(Boolean).map(x => String(x).trim()))]
      .filter(Boolean).sort((a, b) => b.length - a.length);
    if (!clean.length) return null;
    // Whole Unicode words only: EV does not highlight `Achieve` or `Evolve`.
    try {
      return new RegExp(`(?<![\\p{L}\\p{N}_])(?:${clean.map(escapeRegExp).join("|")})(?![\\p{L}\\p{N}_])`, "giu");
    } catch { return null; }
  }, [terms]);
  if (!matcher || !content) return <span className={className}>{content}</span>;
  const nodes = [];
  let start = 0;
  for (const match of content.matchAll(matcher)) {
    const at = match.index;
    if (at > start) nodes.push(content.slice(start, at));
    nodes.push(<mark key={`mark-${at}`} className="rounded-sm bg-amber-200/85 px-[1px] text-slate-900">{match[0]}</mark>);
    start = at + match[0].length;
  }
  if (start < content.length) nodes.push(content.slice(start));
  return <span className={className}>{nodes}</span>;
}

function SearchableDropdown({ label, options = [], selected = [], onChange, multiple = true,
  placeholder = "Select...", showLabel = true, singleLineChips = false }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [visibleLimit, setVisibleLimit] = useState(80);
  const hostRef = useRef(null);
  const menuRef = useRef(null);
  const [placement, setPlacement] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    const rect = hostRef.current?.getBoundingClientRect();
    if (rect) setPlacement({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - Math.max(rect.width, 230) - 8)),
      top: window.innerHeight - rect.bottom > 290 ? rect.bottom + 4 : Math.max(8, rect.top - 285),
      width: Math.max(230, rect.width),
    });
    const onDown = e => {
      if (!hostRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) setOpen(false);
    };
    const onScroll = e => { if (!menuRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);
  // Search the COMPLETE option catalog; render it progressively to avoid
  // thousands of DOM nodes while still allowing the user to scroll to the end.
  const filtered = useMemo(() => (options || []).filter(x =>
    String(x).toLocaleLowerCase().includes(term.toLocaleLowerCase())), [options, term]);
  const visibleOptions = useMemo(() => filtered.slice(0, visibleLimit), [filtered, visibleLimit]);
  useEffect(() => { setVisibleLimit(80); }, [term, options]);
  const onMenuScroll = event => {
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 75) {
      setVisibleLimit(limit => Math.min(limit + 80, filtered.length));
    }
  };
  const toggle = (value) => {
    if (!multiple) { onChange(selected.includes(value) ? [] : [value]); setOpen(false); setTerm(""); return; }
    onChange(selected.includes(value) ? selected.filter(x => x !== value) : [...selected, value]);
  };
  return (
    <div className="relative" ref={hostRef}>
      {showLabel && <div className="mb-1 flex min-h-[19px] items-center gap-2">
        <label className="text-[11px] font-bold text-slate-600">{label}</label>
      </div>}
      <button type="button" onClick={() => setOpen(x => !x)}
        aria-label={`${label}: ${selected.length ? `${selected.length} selected` : placeholder}`}
        aria-expanded={open}
        className="flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 text-left text-[11px] text-slate-600 transition hover:border-[#365561]/40 focus:outline-none focus:ring-2 focus:ring-[#365561]/10">
        <span className="min-w-0 truncate">{selected.length ? `${selected.length} selected` : placeholder}</span>
        <ChevronDown size={13} className="shrink-0" />
      </button>
      {selected.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-start gap-1.5">
          {selected.map(x => (
            <button key={x} type="button" title={`Remove ${x}`}
              aria-label={`Remove ${x} from ${label}`}
              onClick={() => onChange(selected.filter(y => y !== x))}
              className="group inline-flex max-w-full min-w-0 items-center gap-1 rounded-md border border-[#365561]/15 bg-[#365561]/[0.07] py-0.5 pl-1.5 pr-1 text-[10px] font-medium text-[#365561] transition hover:border-[#365561]/35 hover:bg-[#365561]/[0.12]">
              <span className={cls("min-w-0 text-left leading-4", singleLineChips ? "truncate whitespace-nowrap" : "whitespace-normal break-words [overflow-wrap:anywhere]")}>{x}</span>
              <span className="flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-sm bg-[#D4E3E8] text-[#365561] group-hover:bg-[#BDD1D9]">
                <X size={11} strokeWidth={2.7} aria-hidden="true"/>
              </span>
            </button>
          ))}
        </div>
      )}
      {open && placement && createPortal(
        <div ref={menuRef} style={{ position: 'fixed', ...placement }} className="z-[9930] min-w-[230px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_15px_40px_rgba(15,23,42,0.18)]">
          <div className="border-b border-slate-100 p-2">
            <div className="flex items-center gap-1.5 rounded-lg bg-slate-50 px-2 py-1.5">
              <Search size={13} className="text-slate-400" />
              <input autoFocus value={term} onChange={e => setTerm(e.target.value)} placeholder={`Search ${label.toLowerCase()}`}
                className="min-w-0 flex-1 border-0 bg-transparent p-0 text-xs text-slate-700 outline-none" />
            </div>
          </div>
          <div className="max-h-[228px] overflow-y-auto overscroll-contain p-1" onScroll={onMenuScroll}>
            {!filtered.length && <div className="p-2 text-xs text-slate-400">No matching options</div>}
            {visibleOptions.map(value => {
              const active = selected.includes(value);
              return <button type="button" key={value} onClick={() => toggle(value)}
                className={cls("flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-[11px] hover:bg-slate-100", active && "bg-[#365561]/10 text-[#365561]") }>
                <span>{value}</span>{active && <Check size={13} className="shrink-0" />}
              </button>;
            })}
            {visibleLimit < filtered.length && <div className="py-2 text-center text-[10px] text-slate-400">
              Scroll for more · {filtered.length - visibleLimit} remaining
            </div>}
          </div>
        </div>, document.body
      )}
    </div>
  );
}

function FilterToggle({ checked, onChange, children, info }) {
  return (
    <label title={info || ""} className="flex cursor-pointer items-center justify-between gap-2 text-[11px] text-slate-700">
      <span>{children}</span>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)}
        className="h-3.5 w-3.5 shrink-0 accent-[#365561]" />
    </label>
  );
}

function ExperienceRange({ value, onChange }) {
  const min = Number(value?.min ?? 0);
  const max = Number(value?.max ?? 50);
  const [activeThumb, setActiveThumb] = useState("min");
  const minChange = n => { setActiveThumb("min"); onChange({ ...value, min: Math.min(Number(n), max) }); };
  const maxChange = n => { setActiveThumb("max"); onChange({ ...value, max: Math.max(Number(n), min) }); };
  return (
    <div className="space-y-3 rounded-xl bg-white p-3 shadow-[0_3px_12px_rgba(15,23,42,0.08)]">
      <FilterToggle checked={value.enabled} onChange={b => onChange({ ...value, enabled: b })}>Apply experience range</FilterToggle>
      <div className="flex items-center justify-between text-[11px]">
        <span className="font-semibold text-[#365561]">{min}–{max} years</span>
        <span className="text-slate-400">0 to 50</span>
      </div>
      <div className="relative mx-2 h-7 select-none">
        <div className="absolute inset-x-0 top-[12px] h-[5px] rounded-full bg-slate-200" />
        <div className="absolute top-[12px] h-[5px] rounded-full bg-[#365561]"
          style={{ left: `${min * 2}%`, width: `${(max - min) * 2}%` }} />
        {/* Two input thumbs on one shared rail. Invisible tracks allow pointer access. */}
        <input aria-label="Minimum years of experience" type="range" min="0" max="50" step="1"
          value={min} onChange={e => minChange(e.target.value)} onPointerDown={() => setActiveThumb("min")}
          className="dbv2-range absolute inset-x-0 top-[3px] h-6 w-full appearance-none bg-transparent"
          style={{ zIndex: activeThumb === "min" ? 4 : 3 }} />
        <input aria-label="Maximum years of experience" type="range" min="0" max="50" step="1"
          value={max} onChange={e => maxChange(e.target.value)} onPointerDown={() => setActiveThumb("max")}
          className="dbv2-range absolute inset-x-0 top-[3px] h-6 w-full appearance-none bg-transparent"
          style={{ zIndex: activeThumb === "max" ? 4 : 3 }} />
      </div>
      <FilterToggle checked={value.includeUnknown} onChange={b => onChange({ ...value, includeUnknown: b })}>
        Include profiles with unknown experience
      </FilterToggle>
    </div>
  );
}

function RateSlider({ value, onChange, ceiling, setCeiling }) {
  const maximum = Number(value?.max ?? 1500);
  return (
    <div className="space-y-2 rounded-xl bg-white p-3 shadow-[0_3px_12px_rgba(15,23,42,0.08)]">
      <FilterToggle checked={value.enabled} onChange={b => onChange({ ...value, enabled: b })}>Apply rate filter</FilterToggle>
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 whitespace-nowrap text-[11px] font-bold text-[#365561]">0 – {maximum.toLocaleString()}</span>
        <div className="flex shrink-0 items-center gap-1">
          <select aria-label="Rate currency" value={value.currency} onChange={e => onChange({ ...value, currency: e.target.value })}
            className="rounded-md border border-slate-200 bg-white px-1 py-0.5 text-[10px] outline-none">
            {['EUR', 'USD', 'GBP', 'CHF', 'IDR'].map(c => <option key={c}>{c}</option>)}
          </select>
          <select aria-label="Rate frequency" value={value.unit} onChange={e => onChange({ ...value, unit: e.target.value })}
            className="rounded-md border border-slate-200 bg-white px-1 py-0.5 text-[10px] outline-none">
            <option value="hourly">Hourly</option><option value="daily">Daily</option>
          </select>
        </div>
      </div>
      <input type="range" min="0" max={ceiling} step="50" value={Math.min(maximum, ceiling)}
        onChange={e => {
          const next = Number(e.target.value);
          onChange({ ...value, max: next });
          if (next >= ceiling) setCeiling(n => n + 500);
        }} className="h-1.5 w-full cursor-pointer accent-[#365561]" aria-label="Maximum consultant rate" />
      <div className="flex justify-between text-[10px] text-slate-400">
        <span>0</span><span>Slider ceiling {ceiling.toLocaleString()}</span>
      </div>
      <FilterToggle checked={value.includeMissing} onChange={b => onChange({ ...value, includeMissing: b })}>
        Include profiles without rate
      </FilterToggle>
    </div>
  );
}


function EditablePhraseList({ label, values = [], onChange, placeholder }) {
  const [input, setInput] = useState('');
  const add = () => {
    const phrase = input.trim().replace(/\s+/g, ' ');
    if (!phrase || phrase.length > 250 || values.length >= 100) return;
    if (!values.some(x => x.toLocaleLowerCase() === phrase.toLocaleLowerCase())) {
      onChange([...values, phrase]);
    }
    setInput('');
  };
  return <div className="space-y-2">
    <input value={input} onChange={e => setInput(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
      placeholder={placeholder || 'Type a phrase and press Enter'}
      aria-label={label} maxLength={250}
      className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-[11px] outline-none focus:border-[#365561]/50"/>
    <div className="text-[10px] font-semibold text-slate-600">{label} · {values.length} selected (OR)</div>
    <div className="flex max-h-[115px] flex-wrap gap-1.5 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2">
      {values.length ? values.map((name, index) => <button key={`${name}-${index}`} type="button"
        title={`Remove ${name}`} onClick={() => onChange(values.filter((_, i) => i !== index))}
        className="inline-flex max-w-full items-center gap-1 rounded-lg border border-[#C8DADF] bg-[#E7F0F3] px-2 py-1 text-[10px] font-medium text-[#365561]">
        <span className="max-w-[180px] truncate">{name}</span><X size={11}/>
      </button>) : <span className="text-[10px] text-slate-400">No phrases selected</span>}
    </div>
  </div>;
}

function NamedSecondStage({ value, onChange, kind }) {
  const isCompany = kind === 'company';
  const listKey = isCompany ? 'names' : 'titles';
  return <div className="space-y-3 rounded-xl bg-white p-3 shadow-[0_3px_12px_rgba(15,23,42,0.08)]">
    <FilterToggle checked={!!value?.enabled} onChange={enabled => onChange({ ...value, enabled })}>
      {isCompany ? 'Apply company filter' : 'Apply title filter'}
    </FilterToggle>
    <EditablePhraseList label={isCompany ? 'Company names' : 'Job titles'}
      values={value?.[listKey] || []} onChange={items => onChange({ ...value, [listKey]: items })}
      placeholder={isCompany ? 'Add company, press Enter' : 'Add job title, press Enter'}/>
    {!isCompany && <FilterToggle checked={value?.currentOnly !== false}
      onChange={currentOnly => onChange({ ...value, currentOnly })}>
      Include only current positions
    </FilterToggle>}
  </div>;
}

function FilterSection({ title, icon: Icon, children, compact = false }) {
  return <section className={cls(compact ? 'space-y-1' : 'space-y-2.5')}>
    <div className="flex items-center gap-2 text-[11px] font-bold text-[#365561]"><Icon size={14} />{title}</div>
    {children}
  </section>;
}

// Project-side filters share the same shape, spacing, and empty-state treatment.
// The title/icon sit outside the tile, like the existing second-stage filters.
function ProjectFilterTile({ title, icon, emptyMessage, selected, children }) {
  return <FilterSection title={title} icon={icon} compact>
    <div className="space-y-2 rounded-xl bg-white p-3 shadow-[0_3px_12px_rgba(15,23,42,0.08)]">
      {children}
      {!selected?.length && <p className="text-[10px] leading-4 text-slate-400">{emptyMessage}</p>}
    </div>
  </FilterSection>;
}

function SidebarActionButton({ label, disabled, onClick, children, side = 'middle' }) {
  const buttonRef = useRef(null);
  const [tip, setTip] = useState(null);
  const show = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) setTip({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
  };
  const hide = () => setTip(null);
  return <>
    <button ref={buttonRef} type="button" title={label} aria-label={label}
      disabled={disabled} onClick={onClick} onMouseEnter={show} onMouseLeave={hide}
      onFocus={show} onBlur={hide}
      className={cls('flex h-7 w-8 items-center justify-center text-[#365561] hover:bg-[#E7F0F3] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#365561] disabled:opacity-40',
        side === 'left' ? 'rounded-l-lg' : side === 'right' ? 'rounded-r-lg' : '')}>
      {children}
    </button>
    {tip && !disabled && createPortal(
      <div role="tooltip" style={{ position: 'fixed', ...tip, zIndex: 9950 }}
        className="pointer-events-none max-w-[245px] rounded-md bg-[#365561] px-2.5 py-1.5 text-[10px] font-medium leading-4 text-white shadow-lg">
        {label}
      </div>, document.body)}
  </>;
}

function FiltersSidebar({ filters, setFilters, options, rateCeiling, setRateCeiling,
  onFilterAction, filterActionBusy }) {
  const change = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const changeNested = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  return (
    <aside className="relative flex h-full min-h-0 flex-col bg-[linear-gradient(to_bottom,#f8fafc,#eef2f6)] shadow-[4px_0_15px_rgba(15,23,42,0.07)]">
      <div className="relative z-10 flex h-[64px] shrink-0 items-center justify-between bg-[#f8fafc] px-3 shadow-[0_2px_8px_rgba(15,23,42,0.07)]">
        <div className="flex items-center gap-2 text-xs font-extrabold text-[#365561]"><Filter size={15}/>Search filters</div>
        <div className="inline-flex rounded-lg bg-white shadow-sm ring-1 ring-slate-200/80">
          <SidebarActionButton label="Import from LinkedIn search" disabled={!!filterActionBusy}
            onClick={() => onFilterAction('import')} side="left">
            {filterActionBusy === 'import' ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>}
          </SidebarActionButton>
          <div className="h-7 w-px bg-slate-200" aria-hidden="true"/>
          <SidebarActionButton label="Reset search filters" disabled={!!filterActionBusy}
            onClick={() => onFilterAction('reset')} side="right">
            {filterActionBusy === 'reset' ? <Loader2 size={14} className="animate-spin"/> : <RotateCcw size={14}/>}
          </SidebarActionButton>
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-visible px-3.5 py-4">
        <FilterSection title="Profile" icon={UserRound} compact>
          <div className="space-y-3 rounded-xl bg-white p-3 shadow-[0_3px_12px_rgba(15,23,42,0.08)]">
            <FilterToggle checked={filters.verifiedOnly} onChange={v => change('verifiedOnly', v)}>Verified profiles only</FilterToggle>
            {/* Independent consultant checkbox intentionally hidden; use the Title filter instead.
              <FilterToggle checked={filters.independentOnly} onChange={v => change('independentOnly', v)}>
                Independent consultants only
              </FilterToggle>
            */}
            <div className="flex gap-1.5">
              {[['con', 'Consultants'], ['exp', 'Experts']].map(([v, label]) => {
                const chosen = filters.profileTypes.includes(v);
                return <button key={v} type="button"
                  onClick={() => change('profileTypes', chosen ? filters.profileTypes.filter(x => x !== v) : [...filters.profileTypes, v])}
                  className={cls('flex-1 rounded-lg border px-1.5 py-2 text-[10px] font-semibold transition', chosen ? 'border-[#365561] bg-[#365561]/10 text-[#365561]' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300')}>
                  {label}
                </button>;
              })}
            </div>
            <p className="flex items-start gap-1.5 text-[10px] leading-4 text-slate-400">
              <Info size={12} className="mt-0.5 shrink-0" aria-hidden="true"/>
              <span>Please use Title filter for more accurate consultant results</span>
            </p>
          </div>
        </FilterSection>
        <ProjectFilterTile title="Category" icon={Layers} selected={filters.categories}
          emptyMessage="No category mentioned">
          <SearchableDropdown label="Category" options={options.categories} multiple={false}
            showLabel={false} singleLineChips selected={filters.categories} onChange={v => change('categories', v)}
            placeholder="Any category" />
        </ProjectFilterTile>
        <ProjectFilterTile title="Industries" icon={Factory} selected={filters.industries}
          emptyMessage="No industry mentioned">
          <SearchableDropdown label="Industries" options={options.industries} showLabel={false}
            selected={filters.industries} onChange={v => change('industries', v)} placeholder="Any industry" />
        </ProjectFilterTile>
        <ProjectFilterTile title="Skills" icon={Wrench} selected={filters.skills}
          emptyMessage="No skill mentioned">
          <SearchableDropdown label="Skills" options={options.skills} showLabel={false}
            selected={filters.skills} onChange={v => change('skills', v)} placeholder="Any skill" />
        </ProjectFilterTile>
        <ProjectFilterTile title="Languages" icon={Languages} selected={filters.languages}
          emptyMessage="No language mentioned">
          <SearchableDropdown label="Languages" options={options.languages} showLabel={false}
            selected={filters.languages || []} onChange={v => change('languages', v)} placeholder="Any language" />
          {(filters.languages || []).length > 1 && <label className="flex items-center justify-between gap-2 text-[10px] text-slate-600">Language matching
            <select value={filters.languagesMode || 'all'} onChange={e => change('languagesMode', e.target.value)}
              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px]">
              <option value="all">All selected (AND)</option><option value="any">Any selected (OR)</option>
            </select>
          </label>}
          {!!options.projectLanguages?.length && <div className="text-[10px] leading-4 text-slate-500"
            title="Languages explicitly marked mandatory in this project are applied by default; optional languages can be selected manually.">
            Project languages: {options.projectLanguages.map(item => `${item.name}${/^(mandatory|required|must|compulsory|essential|obligatory|yes|true|1)/i.test(String(item.requirement || '').trim()) ? ' (required)' : ''}`).join(', ')}
          </div>}
        </ProjectFilterTile>
        <ProjectFilterTile title="Countries" icon={Globe2} selected={filters.countries}
          emptyMessage="No country mentioned">
          <SearchableDropdown label="Countries" options={options.countries} showLabel={false}
            selected={filters.countries} onChange={v => change('countries', v)} placeholder="Any country" />
        </ProjectFilterTile>
        <ProjectFilterTile title="Regions" icon={MapPin} selected={filters.regions}
          emptyMessage="No region mentioned">
          <SearchableDropdown label="Regions" options={options.regions} showLabel={false}
            selected={filters.regions} onChange={v => change('regions', v)} placeholder="Any region" />
        </ProjectFilterTile>
        <FilterSection title="Companies" icon={Building2} compact>
          <NamedSecondStage label="Companies" kind="company" value={filters.companyFilter || { enabled: false, names: [] }}
            onChange={v => changeNested('companyFilter', v)}/>
        </FilterSection>
        <FilterSection title="Job titles" icon={BriefcaseBusiness} compact>
          <NamedSecondStage label="Job titles" kind="title" value={filters.titleFilter || { enabled: false, titles: [], currentOnly: true }}
            onChange={v => changeNested('titleFilter', v)}/>
        </FilterSection>
        <FilterSection title="Rate" icon={SlidersHorizontal} compact>
          <RateSlider value={filters.rate} onChange={v => changeNested('rate', v)} ceiling={rateCeiling} setCeiling={setRateCeiling}/>
        </FilterSection>
        <FilterSection title="Seniority" icon={BriefcaseBusiness} compact>
          <ExperienceRange value={filters.experience} onChange={v => changeNested('experience', v)}/>
        </FilterSection>
      </div>
    </aside>
  );
}

function ProfileImage({ src, name }) {
  const resolved = resolveCrmImage(src);
  return <img src={resolved || expertDefaultImg}
    alt={name || 'Expert'} loading="lazy" onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = expertDefaultImg; }}
    className="h-[52px] w-[52px] shrink-0 rounded-xl border border-slate-200 bg-white object-cover" />;
}

function CompanyLogo({ company, terms }) {
  const resolved = resolveCrmImage(company.logo);
  return <div title={`${company.name || 'Company'}${company.designation ? ` — ${company.designation}` : ''}`}
    className="flex min-h-[35px] max-w-[130px] items-center gap-1.5 rounded-lg border border-slate-200/70 bg-white px-1.5 py-1">
    <img src={resolved || industryLogo} loading="lazy" alt={`${company.name || 'Company'} logo`}
      onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = industryLogo; }}
      className="h-6 w-8 shrink-0 object-contain" />
    <span className="max-w-[79px] truncate text-[10px] font-semibold text-slate-600"><Highlight text={company.name} terms={terms}/></span>
  </div>;
}

function ExpertRow({ expert, terms, onAdd, adding, addDisabled, onDetail, expanded, detail, detailLoading }) {
  const [summaryScroll, setSummaryScroll] = useState(false);
  const already = !!expert.alreadyInProject;
  return (
    <article className="overflow-hidden rounded-2xl bg-white shadow-[0_4px_18px_rgba(15,23,42,0.09)] transition-shadow hover:shadow-[0_9px_26px_rgba(15,23,42,0.15)]">
      <div className="flex items-start gap-3 px-3 py-3">
        <a href={`/app/ExpertDetails/${encodeURIComponent(expert.id)}`} target="_blank" rel="noopener noreferrer"
          title={`Open ${expert.name || 'expert'} details in a new tab`}
          className="shrink-0 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#365561]">
          <ProfileImage src={expert.profilePicture} name={expert.name}/>
        </a>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <strong className="truncate text-[13px] text-slate-800"><Highlight text={expert.name} terms={terms}/></strong>
            {expert.verified && <ShieldCheck size={14} className="shrink-0 text-emerald-600" aria-label="Verified" />}
            {expert.ranking && <span className="shrink-0 rounded-lg border border-[#C8DADF] bg-[#E7F0F3] px-2.5 py-1 text-[9px] font-bold text-[#365561]"
              title="Relative match score combining project skills, category history and industry history; not a probability">
              #{expert.ranking.position} · {Math.round(expert.ranking.score)}% overall match
            </span>}
            {expert.experienceYears !== null && expert.experienceYears !== undefined && (
              <span className="shrink-0 rounded-full bg-[#365561]/10 px-1.5 py-0.5 text-[9px] font-bold text-[#365561]"
                title={expert.experienceSource === 'work_history_estimate' ? 'Estimated from work-history job durations' : 'Experience source unavailable'}>
                {expert.experienceYears} yrs{expert.experienceSource === 'work_history_estimate' ? ' est.' : ''}
              </span>
            )}
          </div>
          <p className="truncate text-[11px] font-medium text-slate-600"><Highlight text={expert.position} terms={terms}/></p>
          {expert.ranking && <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-semibold" aria-label="Project relevance breakdown">
            {expert.ranking.skillRequired > 0 && <span className="rounded-lg bg-sky-50 px-1.5 py-0.5 text-sky-800"
              title="Unique project-required skills found in human-curated expert skill tags">
              Skills {expert.ranking.skillMatches}/{expert.ranking.skillRequired}</span>}
            {expert.ranking.categoryTotalProjects > 0 && <span className="rounded-lg bg-amber-50 px-1.5 py-0.5 text-amber-800"
              title="Distinct previous projects in the selected category / all other projects in that category">
              Category {expert.ranking.categoryProjects}/{expert.ranking.categoryTotalProjects} projects</span>}
            {expert.ranking.industryTotalProjects > 0 && <span className="rounded-lg bg-teal-50 px-1.5 py-0.5 text-teal-800"
              title="Distinct previous projects in selected industries / all other projects in those industries">
              Industries {expert.ranking.industryProjects}/{expert.ranking.industryTotalProjects} projects</span>}
          </div>}
          <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
            <span className="flex items-center gap-1"><MapPin size={11}/>{[expert.city, expert.country].filter(Boolean).join(', ') || expert.location || 'Location unavailable'}</span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5">{expert.profileType || 'Profile'}</span>
            {expert.employmentClass === 'independent' && <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">Likely independent</span>}
          </div>
        </div>
        <button type="button" title={already ? 'Already in this project' : 'Add to project'}
          onClick={() => !already && !addDisabled && onAdd(expert)} disabled={already || addDisabled}
          className={cls('inline-flex h-8 shrink-0 items-center gap-1 rounded-lg border px-2 text-[11px] font-bold transition',
            already ? 'cursor-default border-emerald-200 bg-emerald-50 text-emerald-700' :
            'border-[#365561]/20 bg-[#365561] text-white hover:bg-[#2c4650] disabled:opacity-50')}>
          {adding ? <Loader2 size={13} className="animate-spin"/> : already ? <Check size={13}/> : <Plus size={13}/>}
          <span className="hidden sm:inline">{already ? 'Added' : 'Add'}</span>
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-100 px-3 py-2">
        {expert.companies?.length ? expert.companies.map((company, index) =>
          <CompanyLogo key={`${expert.id}-${index}`} company={company} terms={terms} />)
          : <span className="text-[10px] italic text-slate-400">No company history available</span>}
      </div>
      <div className="px-3 pb-2.5">
        <div className="max-h-[85px] min-h-[37px] overflow-y-auto rounded-lg bg-[linear-gradient(to_bottom,#f8fafc,#eef2f6)] px-2.5 py-2 text-[11px] leading-[17px] text-slate-600"
          onScroll={() => setSummaryScroll(true)} aria-label="Scrollable consultant summary">
          {expert.summary ? <Highlight text={expert.summary} terms={terms}/> : <span className="italic text-slate-400">No summary available</span>}
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 px-3 py-1.5">
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          {expert.hourlyRate != null && <span>{expert.hourlyRate} {expert.hourlyCurrency || ''}/h</span>}
          {expert.dailyRate != null && <span>{expert.dailyRate} {expert.dailyCurrency || ''}/day</span>}
        </div>
        <button type="button" onClick={() => onDetail(expert.id)}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold text-[#365561] hover:bg-[#365561]/10">
          {expanded ? 'Less detail' : 'More detail'} {expanded ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
        </button>
      </div>
      {expanded && <div className="space-y-2 border-t border-slate-100 bg-slate-50/70 px-3 py-3 text-[11px] text-slate-600">
        {detailLoading ? <Loader2 size={17} className="animate-spin text-[#365561]"/> : detail ? <>
          <p className="font-bold text-slate-700">Professional experience</p>
          <div className="max-h-40 overflow-y-auto leading-5 whitespace-pre-wrap"><Highlight text={detail.experience || 'Not available'} terms={terms}/></div>
          <p className="font-bold text-slate-700">Employment history</p>
          {detail.companies?.slice(0, 12).map((c, i) => <p key={i}><strong>{c.name}</strong> · {c.designation} {c.current && '· Current'}</p>)}
        </> : <span>Could not load profile details.</span>}
      </div>}
    </article>
  );
}

export default function DatabaseSearchDrawer({ projectID, userID, onClose, onAddToProject }) {
  const { API_URL } = useContext(UrlContext);
  const apiRoot = useMemo(() => normalizeApiRoot(API_URL), [API_URL]);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [ready, setReady] = useState(false);
  const [project, setProject] = useState(null);
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [filters, setFilters] = useState(null);
  const [databaseFiltersID, setDatabaseFiltersID] = useState(null);
  const [results, setResults] = useState({ items: [], total: null, hasMore: false, nextCursor: null, highlightTerms: [] });
  const [querying, setQuerying] = useState(false);
  const [paging, setPaging] = useState(false);
  const [error, setError] = useState('');
  const [rateCeiling, setRateCeiling] = useState(1500);
  const [expanded, setExpanded] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [addingID, setAddingID] = useState(null);
  const [actionError, setActionError] = useState('');
  const [filterActionBusy, setFilterActionBusy] = useState(null);
  const [filterActionNotice, setFilterActionNotice] = useState('');
  const [retry, setRetry] = useState(0);
  const [closing, setClosing] = useState(false);
  const requestClose = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    // Flush a pending preference edit when the drawer is closed quickly.
    if (filters && userID && persistenceKey && persistenceKey !== persistedSnapshot.current) {
      const body = { projectID, userID, databaseFiltersID, filters: { ...filters, ...JSON.parse(persistenceKey) } };
      saveQueue.current = saveQueue.current.catch(() => {}).then(() =>
        requestJson('/filters', { method: 'POST', body }).catch(err => console.warn('Could not save drawer settings:', err))
      );
    }
    setClosing(true);
  };
  const skipFirstQuery = useRef(false);
  const persistedSnapshot = useRef(null);
  const saveQueue = useRef(Promise.resolve());
  const mostRecentSaved = useRef(null);
  const saveTimer = useRef(null);
  const activeRequest = useRef(null);
  const requestSequence = useRef(0);
  const listRef = useRef(null);
  const bottomRef = useRef(null);
  const cached = useRef(new Map());
  const resultsRef = useRef(results);
  useEffect(() => { resultsRef.current = results; }, [results]);

  const requestJson = useCallback(async (path, { method = 'GET', body, signal } = {}) => {
    const response = await fetch(`${apiRoot}${path}`, {
      method, signal,
      headers: { 'Content-Type': 'application/json', 'x-access-token': readToken() },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok || json.status !== 'success') throw new Error(json.message || `Request failed (${response.status})`);
    return json;
  }, [apiRoot]);

  // Initialize project-specific preselected categories, industries, skills AND first 30 rows.
  useEffect(() => {
    const ctrl = new AbortController();
    setBootstrapping(true); setReady(false); setError('');
    setDatabaseFiltersID(null);
    setResults({ items: [], total: null, hasMore: false, nextCursor: null, highlightTerms: [] });
    cached.current.clear();
    requestJson(`/options/${projectID}`, { signal: ctrl.signal })
      .then(json => {
        const catalog = json.options || EMPTY_OPTIONS;
        setOptions({ ...catalog, industries: (catalog.industries || []).filter(validIndustryOption) });
      })
      .catch(e => { if (e.name !== 'AbortError') console.warn('Search V2 option catalog unavailable:', e.message); });
    requestJson(`/bootstrap/${projectID}?userID=${encodeURIComponent(userID)}`, { signal: ctrl.signal })
      .then(data => {
        const initialFilters = { ...data.filters, skillsMode: 'any', independentOnly: false };
        // If a server/default payload ever used ALL, fetch a new page with OR
        // rather than showing a stale bootstrap result with the wrong semantics.
        const needsSkillModeRefresh = data.filters?.skillsMode === 'all' || !!data.filters?.independentOnly;
        skipFirstQuery.current = !needsSkillModeRefresh;
        setProject(data.project);
        setDatabaseFiltersID(data.databaseFiltersID ?? null);
        // Match the persisted subset; keyword/name and unsupported controls
        // are deliberately not written to DatabaseFilters.
        const saved = data.filters;
        persistedSnapshot.current = JSON.stringify({
          categories: saved.categories, industries: saved.industries, skills: saved.skills,
          languages: saved.languages, countries: saved.countries, regions: saved.regions,
          companyFilter: saved.companyFilter, titleFilter: saved.titleFilter,
          rate: saved.rate, experience: saved.experience,
        });
        mostRecentSaved.current = persistedSnapshot.current;
        setRateCeiling(Math.max(1500, Number(data.filters?.rate?.max || 0)));
        setFilters(initialFilters);
        setResults(needsSkillModeRefresh
          ? { items: [], total: null, hasMore: false, nextCursor: null, highlightTerms: [] }
          : data.results);
        setReady(true);
        if (!needsSkillModeRefresh) cached.current.set(JSON.stringify({
          ...initialFilters,
          companyFilter: initialFilters.companyFilter?.enabled ? initialFilters.companyFilter : { enabled: false, names: [] },
          titleFilter: initialFilters.titleFilter?.enabled ? initialFilters.titleFilter : { enabled: false, titles: [], currentOnly: true },
          rate: initialFilters.rate?.enabled ? initialFilters.rate : { enabled: false, min: 0, max: 1500, unit: 'hourly', currency: 'EUR', includeMissing: true },
          experience: initialFilters.experience?.enabled ? initialFilters.experience : { enabled: false, min: 0, max: 50, includeUnknown: true },
        }), { value: data.results, at: Date.now() });
      })
      .catch(e => { if (e.name !== 'AbortError') setError(e.message); })
      .finally(() => { if (!ctrl.signal.aborted) setBootstrapping(false); });
    return () => ctrl.abort();
  }, [projectID, userID, requestJson, retry]);

  // Saving uses the complete filter state, including disabled controls.
  // Search uses only *active* second-stage controls. Editing a disabled
  // range/list therefore saves it without triggering SQL or invalidating cursors.
  const effectiveFilters = useMemo(() => {
    if (!filters) return null;
    return {
      ...filters,
      // Skills are always OR-matched. No skill-mode UI or backend changes needed.
      skillsMode: 'any',
      independentOnly: false, // The independent-only UI is hidden; never apply a phantom filter.
      companyFilter: filters.companyFilter?.enabled ? filters.companyFilter : { enabled: false, names: [] },
      titleFilter: filters.titleFilter?.enabled ? filters.titleFilter : { enabled: false, titles: [], currentOnly: true },
      rate: filters.rate?.enabled ? filters.rate : { enabled: false, min: 0, max: 1500, unit: 'hourly', currency: 'EUR', includeMissing: true },
      experience: filters.experience?.enabled ? filters.experience : { enabled: false, min: 0, max: 50, includeUnknown: true },
    };
  }, [filters]);
  const filtersKey = useMemo(() => JSON.stringify(effectiveFilters), [effectiveFilters]);
  const persistenceKey = useMemo(() => filters ? JSON.stringify({
    categories: filters.categories, industries: filters.industries, skills: filters.skills,
    languages: filters.languages, countries: filters.countries, regions: filters.regions,
    companyFilter: filters.companyFilter, titleFilter: filters.titleFilter,
    rate: filters.rate, experience: filters.experience,
  }) : null, [filters]);

  const onFilterAction = async action => {
    if (filterActionBusy || !userID || !databaseFiltersID) return;
    setFilterActionBusy(action);
    setActionError('');
    setFilterActionNotice('');
    // Avoid delayed auto-save overwriting the newly imported/reset values.
    if (saveTimer.current) clearTimeout(saveTimer.current);
    try {
      await saveQueue.current.catch(() => {});
      // Preserve any edits not yet saved before importing the five fields.
      // A full reset deliberately discards pending edits instead.
      if (action === 'import' && persistenceKey && persistenceKey !== persistedSnapshot.current) {
        await requestJson('/filters', { method: 'POST', body: {
          projectID, userID, databaseFiltersID, filters: { ...filters, ...JSON.parse(persistenceKey) },
        }});
        persistedSnapshot.current = persistenceKey;
      }
      const response = await requestJson(`/filters/${action}`, {
        method: 'POST', body: { projectID, userID, databaseFiltersID },
      });
      const updated = response.filters;
      // Keep the current free-text query when importing only the five fields;
      // resetting means truly restoring the drawer's initial state.
      const next = action === 'import'
        ? { ...filters, ...updated, name: filters.name, keywords: filters.keywords,
            skillsMode: 'any', languagesMode: filters.languagesMode,
            verifiedOnly: filters.verifiedOnly, independentOnly: false,
            profileTypes: filters.profileTypes }
        : { ...updated, skillsMode: 'any', independentOnly: false };
      const snapshot = JSON.stringify({
        categories: next.categories, industries: next.industries, skills: next.skills,
        languages: next.languages, countries: next.countries, regions: next.regions,
        companyFilter: next.companyFilter, titleFilter: next.titleFilter,
        rate: next.rate, experience: next.experience,
      });
      persistedSnapshot.current = snapshot;
      mostRecentSaved.current = snapshot;
      cached.current.clear();
      if (activeRequest.current) activeRequest.current.abort();
      requestSequence.current += 1;
      setDatabaseFiltersID(response.databaseFiltersID);
      setRateCeiling(Math.max(1500, Number(next.rate?.max || 0)));
      setOptions(old => ({ ...old,
        categories: Array.from(new Set([...(old.categories || []), ...(next.categories || [])])).sort(),
        industries: Array.from(new Set([...(old.industries || []), ...(next.industries || [])]))
          .filter(validIndustryOption).sort(),
        skills: Array.from(new Set([...(old.skills || []), ...(next.skills || [])])).sort(),
      }));
      setFilters(next);
      setFilterActionNotice(action === 'reset'
        ? 'Restored and saved the project defaults.'
        : response.skippedFields?.length
          ? 'Project filters imported. Category was not available in the ProjectFilters model and was kept unchanged.'
          : 'Project filters imported and saved.');
    } catch (err) {
      setActionError(`Unable to ${action === 'reset' ? 'reset' : 'import'} filters: ${err.message}`);
    } finally {
      setFilterActionBusy(null);
    }
  };

  useEffect(() => {
    if (!ready || !filters || !userID || !persistenceKey || persistenceKey === persistedSnapshot.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const payload = { projectID, userID, databaseFiltersID, filters: { ...filters, ...JSON.parse(persistenceKey) } };
      // Serialize writes; older slow requests must never overwrite a newer edit.
      saveQueue.current = saveQueue.current.catch(() => {}).then(() =>
        requestJson('/filters', { method: 'POST', body: payload })
          .then(() => { mostRecentSaved.current = persistenceKey; persistedSnapshot.current = persistenceKey; })
          .catch(err => { setActionError(`Unable to save filter preferences: ${err.message}`); })
      );
    }, 350);
    return () => clearTimeout(saveTimer.current);
  }, [ready, persistenceKey, projectID, userID, databaseFiltersID, requestJson]);

  useEffect(() => {
    if (!ready || !filters) return undefined;
    if (skipFirstQuery.current) { skipFirstQuery.current = false; return undefined; }
    const ctrl = new AbortController();
    if (activeRequest.current) activeRequest.current.abort();
    activeRequest.current = ctrl;
    setQuerying(true);
    const sequence = ++requestSequence.current;
    const duration = 320; // debounce typing and dragging; dropdowns feel immediate.
    const timer = setTimeout(() => {
      setError('');
      const entry = cached.current.get(filtersKey);
      if (entry && Date.now() - entry.at < CACHE_MS) {
        setResults(entry.value); setQuerying(false);
        if (listRef.current) listRef.current.scrollTop = 0;
        return;
      }
      requestJson('/query', { method: 'POST', body: { projectID, filters: effectiveFilters, includeCount: false }, signal: ctrl.signal })
        .then(json => {
          if (sequence !== requestSequence.current) return;
          setResults(json); cached.current.set(filtersKey, { value: json, at: Date.now() });
          if (cached.current.size > 30) cached.current.delete(cached.current.keys().next().value);
          if (listRef.current) listRef.current.scrollTop = 0;
        })
        .catch(e => { if (e.name !== 'AbortError') setError(e.message); })
        .finally(() => { if (sequence === requestSequence.current) setQuerying(false); });
    }, duration);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [filtersKey, ready, projectID, requestJson]);

  const loadMore = useCallback(async () => {
    const current = resultsRef.current;
    if (!effectiveFilters || !current?.hasMore || !current.nextCursor || paging || querying) return;
    setPaging(true);
    try {
      const next = await requestJson('/query', { method: 'POST', body: {
        projectID, filters: effectiveFilters, cursor: current.nextCursor, includeCount: false,
      }});
      setResults(previous => {
        // Guard against an outdated cursor after filters changed.
        if (previous.nextCursor !== current.nextCursor) return previous;
        const seen = new Set(previous.items.map(e => e.id));
        return { ...next, total: previous.total, totalExact: previous.totalExact,
          items: [...previous.items, ...next.items.filter(e => !seen.has(e.id))] };
      });
    } catch (e) { setError(e.message); }
    finally { setPaging(false); }
  }, [filtersKey, paging, querying, projectID, requestJson]);
  useEffect(() => {
    if (!ready || !bottomRef.current || !listRef.current) return undefined;
    const observer = new IntersectionObserver(([entry]) => { if (entry?.isIntersecting) loadMore(); },
      { root: listRef.current, rootMargin: '240px' });
    observer.observe(bottomRef.current);
    return () => observer.disconnect();
  }, [ready, loadMore, results.items.length]);

  const toggleDetail = async id => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (detailCache[id]) return;
    setDetailLoading(true);
    try {
      const json = await requestJson(`/expert/${id}?projectID=${projectID}`);
      setDetailCache(previous => ({ ...previous, [id]: json.expert }));
    } catch (e) { setActionError(e.message); }
    finally { setDetailLoading(false); }
  };
  const addExpert = async expert => {
    if (addingID !== null || expert.alreadyInProject) return;
    if (typeof onAddToProject !== 'function') {
      setActionError('The project add action is not connected.');
      return;
    }
    setAddingID(expert.id);
    setActionError('');
    try {
      const response = await onAddToProject(expert);
      // Only a confirmed backend success (including "already added") updates the card.
      if (response?.status !== 'success') {
        throw new Error(response?.message || 'The profile was not added.');
      }
      setResults(previous => ({ ...previous, items: previous.items.map(x =>
        x.id === expert.id ? { ...x, alreadyInProject: true } : x) }));
      cached.current.clear();
    } catch (e) {
      setActionError(e.message || 'Unable to add this profile.');
    } finally {
      setAddingID(null);
    }
  };

  useEffect(() => {
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const esc = e => { if (e.key === 'Escape') requestClose(); };
    document.addEventListener('keydown', esc);
    return () => { document.body.style.overflow = before; document.removeEventListener('keydown', esc); };
  }, [requestClose]);
  if (typeof document === 'undefined') return null;

  const content = !closing ? (
    <motion.div key="db-search-drawer" className="fixed inset-x-0 bottom-0 top-[78px] z-[9900] flex justify-end" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div className="absolute inset-0 bg-slate-950/45 backdrop-blur-[2px]" onClick={requestClose} />
      <motion.section role="dialog" aria-modal="true" aria-label="Database Search V2"
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 360, damping: 36 }}
        className="relative z-10 flex h-full w-[950px] shrink-0 flex-col overflow-hidden bg-[linear-gradient(to_bottom,#f8fafc,#eef2f6)] shadow-[-20px_0_70px_rgba(15,23,42,0.28)]">
        <style>{`
          .dbv2-range { pointer-events:none; }
          .dbv2-range::-webkit-slider-thumb { pointer-events:auto; appearance:none; width:17px; height:17px; border:3px solid ${INK}; border-radius:50%; background:white; cursor:ew-resize; box-shadow:0 2px 6px #0002; }
          .dbv2-range::-moz-range-thumb { pointer-events:auto; width:12px; height:12px; border:3px solid ${INK}; border-radius:50%; background:white; cursor:ew-resize; }
          .dbv2-range::-webkit-slider-runnable-track { background:transparent; }
          .dbv2-range::-moz-range-track { background:transparent; }
        `}</style>
        <header className="relative z-20 flex h-[65px] shrink-0 items-center justify-between gap-3 bg-white px-3 shadow-[0_2px_10px_rgba(15,23,42,0.09)]">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#365561]/10"><Database size={19} color={INK}/></span>
            <div className="min-w-0"><h2 className="text-[15px] font-extrabold text-[#365561]">Database Search</h2>
              <p className="truncate text-[10px] text-slate-500">{project?.name || `Project #${projectID}`}</p></div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={requestClose} aria-label="Close database search"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-600 shadow-sm ring-1 ring-slate-200/80 transition hover:bg-rose-50 hover:text-rose-600"><X size={17}/></button>
          </div>
        </header>
        {bootstrapping ? <div className="flex flex-1 flex-col items-center justify-center gap-3 text-sm text-slate-500"><Loader2 size={29} className="animate-spin text-[#365561]"/>Loading project filters and matching profiles...</div> :
          error && !ready ? <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center"><p className="text-sm text-rose-600">{error}</p><button onClick={() => setRetry(v => v + 1)} className="rounded-lg bg-[#365561] px-4 py-2 text-xs font-bold text-white">Retry</button></div> : filters && <div className="flex min-h-0 flex-1 overflow-hidden">
            <div className="h-full w-[320px] shrink-0">
              <FiltersSidebar filters={filters} setFilters={setFilters} options={options} rateCeiling={rateCeiling} setRateCeiling={setRateCeiling}
                onFilterAction={onFilterAction} filterActionBusy={filterActionBusy}/>
            </div>
            <div className="flex h-full w-[630px] min-w-0 shrink-0 flex-col">
              {/* This bar is exactly the same height as the sidebar's Search filters bar. */}
              <div className="relative z-10 flex h-[64px] shrink-0 items-center bg-[#f8fafc] px-3 shadow-[0_2px_8px_rgba(15,23,42,0.07)]">
                <div className="grid w-full grid-cols-[minmax(0,1fr)_minmax(0,3fr)] gap-2">
                  <div className="relative"><Search size={14} className="absolute left-3 top-2.5 text-slate-400"/>
                    <input value={filters.name} onChange={e => setFilters(f => ({ ...f, name: e.target.value }))} placeholder="Name..."
                      className="h-9 w-full rounded-xl border border-slate-200 bg-white pl-8 pr-2 text-[11px] outline-none focus:border-[#365561]/45" /></div>
                  <div className="relative"><Search size={14} className="absolute left-3 top-2.5 text-slate-400"/>
                    <input value={filters.keywords} onChange={e => setFilters(f => ({ ...f, keywords: e.target.value }))} placeholder={'Keywords / Boolean: (EV OR Hybrid) AND Battery'}
                      className="h-9 w-full rounded-xl border border-slate-200 bg-white pl-8 pr-2 text-[11px] outline-none focus:border-[#365561]/45" /></div>
                </div>
              </div>
              {/* Counts and warnings stay pinned below the controls; they don't affect header alignment. */}
              <div className="relative z-[1] shrink-0 space-y-2 bg-white/40 px-3 py-2 backdrop-blur-md shadow-[0_2px_7px_rgba(15,23,42,0.04)]">
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
                  <span className="font-bold text-[#365561]">{results.total != null ? `${results.total.toLocaleString()} matching profiles` : 'Matching profiles'}</span>
                  <span>· {results.items.length} loaded</span>
                  {querying && <Loader2 size={12} className="animate-spin text-[#365561]"/>}
                </div>
                {!!error && <div className="rounded-md bg-rose-50/75 px-2 py-1.5 text-[11px] text-rose-700 shadow-sm" role="alert">{error}</div>}
                {!!actionError && <div className="flex justify-between rounded-md bg-amber-50/75 px-2 py-1.5 text-[11px] text-amber-800 shadow-sm" role="alert">{actionError}<button onClick={() => setActionError('')}><X size={12}/></button></div>}
                {!!filterActionNotice && <div className="flex justify-between rounded-md bg-[#E7F0F3]/75 px-2 py-1.5 text-[11px] text-[#365561] shadow-sm" role="status">{filterActionNotice}<button type="button" aria-label="Dismiss notice" onClick={() => setFilterActionNotice('')}><X size={12}/></button></div>}
              </div>
              <div ref={listRef} className="min-h-0 flex-1 bg-[#f8fafc] overflow-y-auto px-3 py-[12px]">
                <div className="grid grid-cols-1 gap-3">
                  {results.items.map(expert => <ExpertRow key={expert.id} expert={expert} terms={results.highlightTerms}
                    onAdd={addExpert} adding={addingID === expert.id} addDisabled={addingID !== null} onDetail={toggleDetail}
                    expanded={expanded === expert.id} detail={detailCache[expert.id]} detailLoading={detailLoading}/>) }
                </div>
                {!querying && !results.items.length && <div className="py-20 text-center text-sm text-slate-500">No matching profiles. Try adjusting your filters.</div>}
                <div ref={bottomRef} className="flex h-12 items-center justify-center">
                  {paging && <><Loader2 size={15} className="mr-2 animate-spin text-[#365561]"/><span className="text-xs text-slate-500">Loading more profiles...</span></>}
                  {!results.hasMore && results.items.length > 0 && <span className="text-[10px] text-slate-400">All matching profiles loaded</span>}
                </div>
              </div>
            </div>
          </div>}
      </motion.section>
    </motion.div>
  ) : null;
  return createPortal(<AnimatePresence onExitComplete={() => onClose?.()}>{content}</AnimatePresence>, document.body);
}
